import { Play } from "lucide-react"
import type { ReactNode } from "react"
import type { Post } from "@/shared/lib/types"
import { cn } from "@/shared/lib/utils"
import { HapticLink } from "@/shared/ui/haptic-link"
import { getPostCover } from "../lib/post-cover"

interface PostPosterCardProps {
	post: Post
	progress?: number | null
	meta?: ReactNode
	className?: string
}

export function PostPosterCard({ post, progress, meta, className }: PostPosterCardProps) {
	const cover = getPostCover(post)
	const format = post.formats[0]
	const caption = meta ?? format?.name

	return (
		<HapticLink
			to={`/post/${post.id}`}
			className={cn(
				"group block w-60 rounded-xl transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
				className,
			)}
		>
			<div className="relative aspect-video overflow-hidden rounded-xl bg-secondary/60 ring-1 ring-white/5">
				{cover ? (
					<img
						src={cover}
						alt=""
						loading="lazy"
						decoding="async"
						className="h-full w-full object-cover"
					/>
				) : (
					<span className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_50%_60%,color-mix(in_oklab,var(--color-primary)_22%,transparent),transparent_65%)] bg-card text-muted-foreground">
						{format?.iconUrl ? (
							<img src={format.iconUrl} alt="" loading="lazy" className="size-20 object-contain" />
						) : (
							<Play className="size-7" />
						)}
					</span>
				)}
				{progress != null && (
					<div className="absolute inset-x-0 bottom-0 h-1 bg-black/50">
						<div
							className="h-full bg-primary"
							style={{ width: `${Math.round(Math.min(Math.max(progress, 0), 1) * 100)}%` }}
						/>
					</div>
				)}
			</div>
			<p className="mt-2 line-clamp-2 text-sm font-medium leading-snug">{post.title}</p>
			{caption && <p className="mt-0.5 truncate text-xs text-muted-foreground">{caption}</p>}
		</HapticLink>
	)
}
